/*
  Klaviyo footer newsletter.

  Progressive enhancement over the existing Shopify {% form 'customer' %}: the
  markup and styling are untouched, we only intercept the submit, send the
  profile to Klaviyo's client subscribe API, and reveal the success message in
  place instead of reloading the page.

  The shopper's language comes from Shopify (request.locale.iso_code), mapped to
  a BCP-47 code in footer.liquid and read off the wrapper's data attributes. It
  is written to the Klaviyo profile's native `locale` attribute, which is what
  Smart Translations and language segments read.

  If JS never runs, the native Shopify form post still works.
*/
(function () {
  'use strict';

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var REVISION = '2026-07-15';

  function show(el) {
    if (el) el.style.display = '';
  }

  function hide(el) {
    if (el) el.style.display = 'none';
  }

  function setError(wrapper, message) {
    var node = wrapper.querySelector('[data-klaviyo-error]');
    if (!node) return;
    var text = node.querySelector('[data-klaviyo-error-text]');
    if (text) text.textContent = message;
    show(node);
  }

  function buildPayload(wrapper, email) {
    var properties = {
      'Signup Source': wrapper.dataset.klaviyoSource || 'Footer Newsletter',
      'Preferred Language': wrapper.dataset.klaviyoLanguage || 'English',
      'Shopify Locale': wrapper.dataset.klaviyoShopifyLocale || ''
    };

    if (wrapper.dataset.klaviyoCountry) {
      properties['Shopify Country'] = wrapper.dataset.klaviyoCountry;
    }

    var attributes = {
      email: email,
      properties: properties
    };

    if (wrapper.dataset.klaviyoLocale) {
      attributes.locale = wrapper.dataset.klaviyoLocale;
    }

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: wrapper.dataset.klaviyoSource || 'Footer Newsletter',
          profile: {
            data: {
              type: 'profile',
              attributes: attributes
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: wrapper.dataset.klaviyoListId
            }
          }
        }
      }
    };
  }

  /*
    Keeps the pre-Klaviyo behaviour alive: the same POST the browser would have
    made natively, so the subscriber still lands in Shopify tagged `newsletter`.
    Fire and forget — a failure here must never block the Klaviyo success state.
  */
  function alsoCreateShopifyCustomer(form) {
    try {
      var body = new FormData(form);
      body.delete('return_to');
      fetch(form.action, {
        method: 'POST',
        body: body,
        headers: { Accept: 'text/html' }
      }).catch(function () {});
    } catch (e) {
      /* no-op */
    }
  }

  function subscribe(wrapper, form) {
    var input = form.querySelector('input[type="email"]');
    var button = form.querySelector('button[type="submit"]');
    var success = wrapper.querySelector('[data-klaviyo-success]');
    var error = wrapper.querySelector('[data-klaviyo-error]');
    var email = (input && input.value || '').trim();

    if (!email) return;

    hide(error);
    hide(success);
    if (button) button.setAttribute('disabled', 'disabled');

    var url = ENDPOINT + '?company_id=' + encodeURIComponent(wrapper.dataset.klaviyoCompanyId);

    // Fire this BEFORE Klaviyo, not after a successful response. If Klaviyo is
    // down we must still capture the subscriber exactly as the theme did before,
    // otherwise a Klaviyo outage silently loses signups.
    if (wrapper.dataset.klaviyoKeepShopifyCustomer === 'true') {
      alsoCreateShopifyCustomer(form);
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/vnd.api+json',
        revision: REVISION
      },
      body: JSON.stringify(buildPayload(wrapper, email))
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Klaviyo responded ' + response.status);

        form.reset();
        show(success);
        if (success && typeof success.focus === 'function') success.focus();

        if (window.klaviyo && typeof window.klaviyo.push === 'function') {
          window.klaviyo.push(['identify', { $email: email }]);
        }
      })
      .catch(function () {
        setError(wrapper, wrapper.dataset.klaviyoErrorMessage || 'Something went wrong. Please try again.');
      })
      .finally(function () {
        if (button) button.removeAttribute('disabled');
      });
  }

  function init(wrapper) {
    if (wrapper.dataset.klaviyoBound === 'true') return;

    // Without a key or list we leave the native Shopify form completely alone.
    if (!wrapper.dataset.klaviyoCompanyId || !wrapper.dataset.klaviyoListId) return;

    var form = wrapper.querySelector('form');
    if (!form) return;

    wrapper.dataset.klaviyoBound = 'true';

    // Native browser validation is left on, so an empty or malformed address
    // still gets the same built-in prompt it does today and never reaches here.
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      subscribe(wrapper, form);
    });
  }

  function initAll() {
    document.querySelectorAll('[data-klaviyo-newsletter]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders the footer section on every setting change.
  document.addEventListener('shopify:section:load', initAll);
})();
